export type ProjectCategory =
  | "eLearning"
  | "SharePoint Apps"
  | "Rise Custom Code Blocks"
  | "Interactive Courses"
  | "3D"
  | "Animation"
  | "Graphic Design"
  | "Illustration";

export type ProjectDemoType = 'html' | 'scorm' | 'storyline' | 'iframe' | 'webapp';

export interface Project {
  id: string;
  title: string;
  slug: string;
  category: string; // Comma-separated categories
  client: string;
  year: string;
  technologies: string[];
  role: string;
  description: string;
  metaDescription?: string;
  longDescription: string;
  thumbnail: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  images: string[];
  demoUrl?: string;
  demoType?: ProjectDemoType;
  embedDemo?: boolean; // If true, embed demo inline; if false, only show Launch Demo button
  codeUrl?: string;
  youtubeUrl?: string;
  downloadUrl?: string;
  codeSnippet?: {
    language: string;
    code: string;
  };
  showCodeSnippet?: boolean;
  hideCodeButton?: boolean;
  featured: boolean;
  /** Set by list queries that skip the snippet code itself. */
  hasCodeSnippet?: boolean;
}

export const projects: Project[] = [
  {
    id: "1",
    title: "Security Awareness Training Module",
    slug: "security-awareness-training",
    category: "eLearning",
    client: "Enterprise Security Corp",
    year: "2024",
    technologies: ["Articulate Storyline", "JavaScript", "SCORM 2004"],
    role: "Lead Developer & Instructional Designer",
    description: "Interactive security awareness training with gamification elements and real-world scenarios.",
    longDescription: `This comprehensive security awareness training module was designed to engage employees through interactive scenarios and gamification. The course features branching scenarios where learners make decisions that affect the outcome, reinforcing the importance of security best practices.

Key features include:
- Interactive phishing simulation exercises
- Real-world case studies with video content
- Knowledge checks with immediate feedback
- Progress tracking and completion certificates
- Mobile-responsive design for learning on-the-go`,
    thumbnail: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&q=80",
      "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&q=80",
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80"
    ],
    demoUrl: "https://example.com/demo",
    featured: true
  },
  {
    id: "2",
    title: "SharePoint Learning Hub",
    slug: "sharepoint-learning-hub",
    category: "SharePoint Apps",
    client: "Global Tech Inc",
    year: "2024",
    technologies: ["SharePoint SPFx", "React", "TypeScript", "Microsoft Graph API"],
    role: "Full Stack Developer",
    description: "Custom SharePoint application for centralized learning management and resource distribution.",
    longDescription: `A custom SharePoint Framework application that transforms the company intranet into a comprehensive learning hub. The solution integrates with Microsoft 365 services to provide a seamless learning experience.

Features implemented:
- Custom web parts for course catalog display
- Integration with Microsoft Teams for live sessions
- Automated notifications via Power Automate
- Analytics dashboard for learning metrics
- Mobile-optimized responsive design`,
    thumbnail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80",
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80"
    ],
    codeUrl: "https://github.com/example/sharepoint-hub",
    codeSnippet: {
      language: "typescript",
      code: `import { WebPartContext } from '@microsoft/sp-webpart-base';
import { MSGraphClient } from '@microsoft/sp-http';

export class LearningHubService {
  private context: WebPartContext;
  
  constructor(context: WebPartContext) {
    this.context = context;
  }
  
  async getCourses(): Promise<ICourse[]> {
    const client = await this.context.msGraphClientFactory
      .getClient();
    
    const response = await client
      .api('/sites/{site-id}/lists/{list-id}/items')
      .expand('fields')
      .get();
      
    return response.value.map(this.mapToCourse);
  }
}`
    },
    featured: true
  },
  {
    id: "3",
    title: "Interactive Code Blocks for Rise",
    slug: "rise-code-blocks",
    category: "Rise Custom Code Blocks",
    client: "Training Solutions Ltd",
    year: "2023",
    technologies: ["HTML5", "CSS3", "JavaScript", "Articulate Rise"],
    role: "Developer",
    description: "Custom interactive code blocks with syntax highlighting and live preview for Rise courses.",
    longDescription: `A collection of custom code blocks designed specifically for Articulate Rise that enable developers to showcase code examples with full syntax highlighting and interactive capabilities.

The solution includes:
- Syntax highlighting for 20+ programming languages
- Copy-to-clipboard functionality
- Line number display
- Interactive code playground
- Dark and light theme options`,
    thumbnail: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&q=80",
      "https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1200&q=80"
    ],
    demoUrl: "https://example.com/rise-demo",
    codeSnippet: {
      language: "javascript",
      code: `function initCodeBlock(element) {
  const code = element.querySelector('code');
  const language = code.dataset.language || 'javascript';
  
  // Apply syntax highlighting
  Prism.highlightElement(code);
  
  // Add copy button
  const copyBtn = document.createElement('button');
  copyBtn.className = 'copy-btn';
  copyBtn.textContent = 'Copy';
  copyBtn.onclick = () => copyToClipboard(code.textContent);
  
  element.appendChild(copyBtn);
}`
    },
    featured: true
  },
  {
    id: "4",
    title: "Compliance Training Course",
    slug: "compliance-training",
    category: "Interactive Courses",
    client: "Healthcare Plus",
    year: "2023",
    technologies: ["Articulate Storyline", "xAPI", "LRS Integration"],
    role: "Instructional Designer",
    description: "HIPAA compliance training with interactive scenarios and assessment tracking.",
    longDescription: `A comprehensive HIPAA compliance training course designed for healthcare professionals. The course uses scenario-based learning to help employees understand and apply privacy regulations in their daily work.`,
    thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80",
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1200&q=80"
    ],
    featured: false
  },
  {
    id: "5",
    title: "Product Visualization - Tech Gadget",
    slug: "3d-product-visualization",
    category: "3D",
    client: "TechStart Innovations",
    year: "2024",
    technologies: ["Blender", "Three.js", "WebGL"],
    role: "3D Artist & Developer",
    description: "Interactive 3D product visualization with realistic materials and lighting.",
    longDescription: `A high-fidelity 3D product visualization created for a consumer electronics company. The interactive model allows customers to explore the product from all angles with realistic materials and lighting effects.`,
    thumbnail: "https://images.unsplash.com/photo-1620321023374-d1a68fbc720d?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1620321023374-d1a68fbc720d?w=1200&q=80",
      "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1200&q=80"
    ],
    demoUrl: "https://example.com/3d-demo",
    featured: true
  },
  {
    id: "6",
    title: "Explainer Animation - Cloud Security",
    slug: "cloud-security-animation",
    category: "Animation",
    client: "CloudSecure Systems",
    year: "2023",
    technologies: ["After Effects", "Lottie", "Illustrator"],
    role: "Motion Designer",
    description: "Engaging explainer animation breaking down complex cloud security concepts.",
    longDescription: `A series of animated explainer videos that break down complex cloud security concepts into digestible, visually engaging content. The animations use consistent branding and a friendly visual style to make technical content accessible.`,
    thumbnail: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&q=80",
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80"
    ],
    featured: false
  },
  {
    id: "7",
    title: "Brand Identity - Cyber Academy",
    slug: "cyber-academy-branding",
    category: "Graphic Design",
    client: "Cyber Academy",
    year: "2024",
    technologies: ["Figma", "Illustrator", "Photoshop"],
    role: "Lead Designer",
    description: "Complete brand identity system for an online cybersecurity education platform.",
    longDescription: `A comprehensive brand identity package for a new online cybersecurity education platform. The design system includes logo variations, color palette, typography guidelines, and application examples across digital and print media.`,
    thumbnail: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&q=80",
      "https://images.unsplash.com/photo-1626785774573-4b799315345d?w=1200&q=80"
    ],
    featured: true
  },
  {
    id: "8",
    title: "Character Design - Security Mascots",
    slug: "security-mascots",
    category: "Illustration",
    client: "SafeNet Training",
    year: "2023",
    technologies: ["Procreate", "Illustrator"],
    role: "Illustrator",
    description: "Custom character designs for security awareness training materials.",
    longDescription: `A set of friendly mascot characters designed to guide learners through security awareness training. Each character represents different security concepts and helps make potentially dry content more engaging and memorable.`,
    thumbnail: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80",
    images: [
      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&q=80",
      "https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=1200&q=80"
    ],
    featured: false
  }
];

export const categories: ProjectCategory[] = [
  "eLearning",
  "SharePoint Apps",
  "Rise Custom Code Blocks",
  "Interactive Courses",
  "3D",
  "Animation",
  "Graphic Design",
  "Illustration"
];
